import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ImageOff, Star, X, Loader2, ThumbsUp, MessageSquare } from 'lucide-react'
import { BrandingBadge } from '../components/BrandingBadge'
import { FilterChips, type FilterKey } from '../components/FilterChips'
import { PhotoSurface } from '../components/PhotoSurface'
import { OpenInAppLink } from '../components/OpenInAppLink'
import { ReviewInbox } from '../components/ReviewInbox'
import { ShotSelectLogo } from '../components/ShotSelectLogo'
import { PolicyDownloadMenu } from '../components/PolicyDownloadMenu'
import { grantedDownloadTiers } from '../../../src/sharePolicy/clientAccess'
import { approveDelivery, trackShareEvent } from '../api/client'
import { useManifest } from '../state/manifest'
import { clientPolicyForManifest } from '../state/capabilities'
import { reportGalleryViewed } from '../state/linkVisit'
import { selectCounts, useSession } from '../state/session'
import { effectiveReviewTarget } from '../utils/reviewTarget'
import type { Photo } from '../types'

function useImageDimensions(photos: Photo[]) {
  const [loaded, setLoaded] = useState<Record<string, number>>({})
  const dimensions = useMemo(
    () =>
      Object.fromEntries(
        photos.map((photo) => [
          photo.id,
          photo.aspectW && photo.aspectH && photo.aspectW > 0 && photo.aspectH > 0
            ? photo.aspectW / photo.aspectH
            : loaded[photo.thumbUrl],
        ])
      ),
    [photos, loaded]
  )
  // Legacy manifests learn dimensions from the visible, lazy-loaded image.
  // No separate Image() probes: offscreen photos should stay off the network.
  const recordDimensions = (url: string, width: number, height: number) => {
    if (width <= 0 || height <= 0) return
    const ratio = width / height
    setLoaded((prev) => (prev[url] === ratio ? prev : { ...prev, [url]: ratio }))
  }
  return { dimensions, recordDimensions }
}

/**
 * `briefVisible` — the photographer's brief is currently covering the gallery,
 * so this render is not yet a view the studio should be told about. Landing
 * passes it; the standalone `/gallery` route leaves it false.
 */
export function Gallery({ briefVisible = false }: { briefVisible?: boolean }) {
  const navigate = useNavigate()
  const manifest = useManifest((s) => s.manifest)
  const picks = useSession((s) => s.picks)
  const shareToken = useSession((s) => s.shareToken)
  const counts = selectCounts(picks)
  const [filter, setFilter] = useState<FilterKey>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  // Deep-linkable person view: `…?t=<token>&person=<name>` opens the gallery
  // already filtered to that person — the "here are your photos" URL a studio
  // texts an aunt, mirroring how face-recognition sharing apps deliver only
  // the recipient's own frames.
  const [personFilter, setPersonFilter] = useState<string>(() => {
    if (typeof window === 'undefined') return 'all'
    const hash = window.location.hash
    const qs = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : ''
    return new URLSearchParams(qs).get('person') ?? 'all'
  })

  const photos = manifest?.photos ?? []
  const { dimensions, recordDimensions } = useImageDimensions(photos)
  const policy = manifest ? clientPolicyForManifest(manifest) : null
  // Stable string so the view effect below never re-fires just because the
  // legacy adapter minted a fresh frozen object on a re-render.
  const downloadTiers = policy ? grantedDownloadTiers(policy).join('+') || 'none' : 'none'

  useEffect(() => {
    document.documentElement.classList.add('light-mode')
    return () => {
      document.documentElement.classList.remove('light-mode')
    }
  }, [])

  // "Viewed gallery" in the studio's link tracking. It stays an ENGAGEMENT
  // signal rather than a second name for "opened the link" (which the worker
  // already records server-side on the manifest fetch): the gallery mounts the
  // moment a link opens, so it waits for the brief to clear, and
  // reportGalleryViewed holds it to one per link per tab — the photo viewer can
  // route the client back here.
  useEffect(() => {
    if (!shareToken || !manifest || briefVisible) return
    reportGalleryViewed(shareToken, {
      photoCount: photos.length,
      picksEnabled: manifest.picksEnabled,
      // Derived from the versioned policy, not a collapsed legacy mode: a v0
      // record reaches the same vocabulary through adaptLegacyManifestPolicy.
      // Bounded tier names only — never a filename, path, or client string.
      downloadTiers,
    })
  }, [briefVisible, downloadTiers, manifest, photos.length, shareToken])

  const categories = useMemo(
    () => Array.from(new Set(photos.map((p) => p.category).filter(Boolean))),
    [photos]
  )

  // "Find yourself": every named person the studio shared, most-frequent
  // first, so the couple leads and the longest tail stays reachable.
  const people = useMemo(() => {
    const counts = new Map<string, number>()
    for (const p of photos)
      for (const name of p.people ?? []) {
        counts.set(name, (counts.get(name) ?? 0) + 1)
      }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name)
  }, [photos])

  // Map photo id → its index in the full gallery array, built once per
  // manifest. The grid below renders a filtered subset but each tile must
  // navigate to the full-array index, so lookups stay O(1) instead of an
  // O(N) findIndex per tile. Mirrors Review.tsx's photoIndexById.
  const photoIndexById = useMemo(() => {
    const map: Record<string, number> = {}
    photos.forEach((p, i) => {
      map[p.id] = i
    })
    return map
  }, [photos])

  const filtered = useMemo(() => {
    let base =
      categoryFilter === 'all' ? photos : photos.filter((p) => p.category === categoryFilter)
    if (personFilter !== 'all') base = base.filter((p) => (p.people ?? []).includes(personFilter))
    if (filter === 'all') return base
    if (filter === 'recommended') return base.filter((p) => p.isRecommended)
    return base.filter((p) => picks[p.id]?.bucket === filter)
  }, [categoryFilter, personFilter, filter, picks, photos])

  const filterCounts = useMemo(
    () => ({
      all: photos.length,
      recommended: photos.filter((p) => p.isRecommended).length,
      love: counts.love,
      maybe: counts.maybe,
      skip: counts.skip,
    }),
    [counts, photos]
  )
  const selectedCount = counts.love + counts.maybe
  const reviewEnabled = policy?.can.select ?? false
  const approveEnabled = policy?.can.approveDelivery ?? false
  const downloadsOn = Boolean(
    policy && (policy.downloads.prepared.length > 0 || policy.downloads.originals.length > 0)
  )

  // Zip-all href: the worker's download-all endpoint, threaded with the studio's
  // tier + the gallery pin (no-ops on an un-pinned link). null when downloads are
  // off or the manifest predates the zipUrl field.
  const zipAvailability = useMemo(
    () => ({
      web:
        photos.length > 0 &&
        photos.every((photo) => photo.downloadAvailability?.web ?? policy?.policyVersion === 0),
      largest:
        photos.length > 0 &&
        photos.every((photo) => photo.downloadAvailability?.largest ?? policy?.policyVersion === 0),
      jpg:
        photos.length > 0 &&
        photos.every((photo) => photo.downloadAvailability?.jpg ?? policy?.policyVersion === 0),
      raw:
        photos.length > 0 &&
        photos.every((photo) => photo.downloadAvailability?.raw ?? policy?.policyVersion === 0),
    }),
    [photos, policy?.policyVersion]
  )

  // Delivery sign-off: a picks-disabled gallery has no Review flow, so the
  // couple's terminal action is Approve / Request changes. Posts a
  // client_link_event the studio sees, then routes to the Done screen.
  const [approving, setApproving] = useState<'approve' | 'request_changes' | null>(null)
  const [approveError, setApproveError] = useState<string | null>(null)
  const sendDecision = async (decision: 'approve' | 'request_changes') => {
    if (!shareToken || approving) return
    setApproving(decision)
    setApproveError(null)
    try {
      await approveDelivery(shareToken, decision)
      navigate('/done')
    } catch {
      setApproveError("We couldn't send that. Check your connection and try again.")
    } finally {
      setApproving(null)
    }
  }
  const reviewTarget = effectiveReviewTarget(manifest?.quotaTarget, photos.length)
  const modeLabel = reviewEnabled
    ? reviewTarget
      ? `${reviewTarget.min}-${reviewTarget.max} review target`
      : 'Pick the ones you want'
    : downloadsOn
      ? 'Download gallery'
      : 'View-only gallery'
  const activeCategoryLabel = categoryFilter === 'all' ? 'All moments' : categoryFilter
  const expiresLabel = useMemo(() => {
    if (!manifest?.expiresAt) return 'Private link'
    const ts = Date.parse(manifest.expiresAt)
    if (!Number.isFinite(ts)) return 'Private link'
    const days = Math.ceil((ts - Date.now()) / 86_400_000)
    if (days <= 0) return 'Expires today'
    return `${days} day${days === 1 ? '' : 's'} left`
  }, [manifest?.expiresAt])

  const heroPhoto = useMemo(() => {
    if (!manifest) return null
    const landscape = manifest.photos.find((p) => p.aspectW && p.aspectH && p.aspectW > p.aspectH)
    return landscape ?? manifest.photos.find((p) => p.isRecommended) ?? manifest.photos[0] ?? null
  }, [manifest])

  if (!manifest) return null
  const goReview = () => navigate('/review')

  return (
    <div className="min-h-[100dvh] bg-[#050505] text-[var(--text-primary)]">
      <div className="mx-auto min-h-[100dvh] w-full max-w-[1440px] bg-[#fbfbfa]">
        {heroPhoto ? (
          <header className="relative min-h-[520px] w-full overflow-hidden bg-black sm:min-h-[620px] lg:min-h-[690px]">
            <img
              src={heroPhoto.displayUrl}
              className="absolute inset-0 h-full w-full object-cover"
              alt={`${manifest.event.title} cover`}
              draggable={false}
            />
            <div className="absolute inset-0 bg-black/48" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.12)_42%,rgba(0,0,0,0.86)_100%)]" />

            <div className="absolute left-5 right-5 top-5 z-20 flex items-center justify-between sm:left-6 sm:right-6 sm:top-6">
              <div className="flex min-w-0 items-center gap-2.5 text-white">
                {manifest.photographer.brandLogoUrl ? (
                  <img
                    src={manifest.photographer.brandLogoUrl}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-lg border border-white/20 bg-white/10 object-contain"
                    draggable={false}
                  />
                ) : (
                  <ShotSelectLogo className="h-5 w-5 shrink-0 sm:h-6 sm:w-6" />
                )}
                <span className="truncate text-[11px] font-bold uppercase tracking-[0.22em] text-white/90">
                  {manifest.photographer.name}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <OpenInAppLink shareToken={shareToken} />
                {policy && manifest.zipUrl && downloadsOn && (
                  <div data-testid="gallery-download-all">
                    <PolicyDownloadMenu
                      policy={policy}
                      availability={zipAvailability}
                      downloadUrl={manifest.zipUrl}
                      onDownload={(tier) => {
                        if (shareToken)
                          void trackShareEvent(shareToken, 'photo_downloaded', {
                            metadata: { source: 'gallery_zip', tier },
                          })
                      }}
                    />
                  </div>
                )}
                {manifest.shareToken && (
                  <ReviewInbox
                    shareToken={manifest.shareToken}
                    onOpenPhoto={(photoId) => {
                      const index = photoIndexById[photoId]
                      if (index !== undefined) navigate(`/photo/${index}`)
                    }}
                  />
                )}
                {reviewEnabled && (
                  <button
                    onClick={goReview}
                    className="h-10 shrink-0 rounded-full border border-white/20 bg-white/18 px-5 text-[11px] font-bold uppercase tracking-[0.16em] text-white shadow-lg backdrop-blur-md transition hover:bg-white/28"
                  >
                    Review Picks
                  </button>
                )}
              </div>
            </div>

            {/* The hero text is decoration above a bar of controls. It grows with
                a long title on a narrow phone instead of riding up under that bar,
                and it never takes a tap meant for Download or Review. */}
            <div className="pointer-events-none relative z-10 flex min-h-[520px] flex-col items-center justify-center px-5 pb-10 pt-24 text-center text-white sm:min-h-[620px] lg:min-h-[690px]">
              <ShotSelectLogo className="mb-7 h-10 w-10 shadow-[0_10px_30px_rgba(0,0,0,0.35)] sm:h-12 sm:w-12" />
              <h1
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                className="max-w-[980px] break-words text-[clamp(2.25rem,5.2vw,4.75rem)] font-semibold uppercase leading-[0.98] tracking-[0.14em] drop-shadow-2xl"
              >
                {manifest.event.title}
              </h1>
              <div className="mt-6 text-[12px] font-semibold uppercase tracking-[0.32em] text-white/90">
                {manifest.event.dateLabel || 'View Gallery'}
              </div>
              <div className="mt-8 grid w-full max-w-3xl grid-cols-3 gap-px overflow-hidden rounded-xl border border-white/15 bg-white/15 text-left backdrop-blur-md">
                <HeroMetric label="Files" value={photos.length.toLocaleString()} />
                <HeroMetric
                  label={reviewEnabled ? 'Picked' : 'Mode'}
                  value={reviewEnabled ? String(selectedCount) : 'Delivery'}
                />
                <HeroMetric label="Link" value={expiresLabel} />
              </div>
            </div>
          </header>
        ) : (
          <header className="bg-[#111] px-6 py-8 text-white">
            <div className="flex items-center gap-3">
              <ShotSelectLogo className="h-6 w-6" />
              <div>
                <h1 className="text-lg font-semibold">{manifest.event.title}</h1>
                <p className="text-xs uppercase tracking-[0.18em] text-white/60">
                  {manifest.photographer.name}
                </p>
              </div>
            </div>
          </header>
        )}

        <section className="sticky top-0 z-30 border-b border-neutral-200/80 bg-[#fbfbfa]/92 px-5 py-4 backdrop-blur-xl sm:px-8 lg:px-10">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500">
                {activeCategoryLabel}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-600">
                <span className="font-semibold text-neutral-950">
                  {filtered.length.toLocaleString()} visible
                </span>
                <span>{modeLabel}</span>
                {reviewEnabled && selectedCount > 0 && (
                  <span className="text-emerald-700">{selectedCount.toLocaleString()} picked</span>
                )}
              </div>
            </div>
            <div className="min-w-0 space-y-2">
              {categories.length > 1 && (
                <div
                  className="flex gap-1.5 overflow-x-auto no-scrollbar xl:justify-end"
                  role="tablist"
                  aria-label="Filter by ceremony"
                  data-testid="gallery-category-chips"
                >
                  {['all', ...categories].map((category) => {
                    const active = categoryFilter === category
                    return (
                      <button
                        key={category}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setCategoryFilter(category)}
                        className={[
                          'h-9 shrink-0 rounded-full border px-3.5 text-xs font-semibold transition-colors',
                          active
                            ? 'border-[#111] bg-[#111] text-white'
                            : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:text-neutral-950',
                        ].join(' ')}
                      >
                        {category === 'all' ? 'All' : category}
                      </button>
                    )
                  })}
                </div>
              )}

              {people.length > 0 && (
                <div
                  className="flex gap-1.5 overflow-x-auto no-scrollbar xl:justify-end"
                  role="tablist"
                  aria-label="Find people"
                  data-testid="gallery-people-chips"
                >
                  {['all', ...people].map((name) => {
                    const active = personFilter === name
                    return (
                      <button
                        key={name}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setPersonFilter(name)}
                        className={[
                          'h-9 shrink-0 rounded-full border px-3.5 text-xs font-semibold transition-colors',
                          active
                            ? 'border-[#111] bg-[#111] text-white'
                            : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:text-neutral-950',
                        ].join(' ')}
                      >
                        {name === 'all' ? 'Everyone' : name}
                      </button>
                    )
                  })}
                </div>
              )}

              {reviewEnabled && (
                <FilterChips active={filter} onChange={setFilter} counts={filterCounts} />
              )}
            </div>
          </div>
        </section>

        <main className="px-5 pb-28 pt-6 sm:px-8 lg:px-10">
          <div className="mb-6 grid gap-2 border-y border-neutral-200 py-3 text-xs text-neutral-600 md:grid-cols-3">
            <GalleryEvidence
              label="Review state"
              value={reviewEnabled ? 'Picks sync back to studio' : 'Approve or request changes'}
            />
            <GalleryEvidence
              label="Download"
              value={
                downloadsOn
                  ? [...policy!.downloads.prepared, ...policy!.downloads.originals]
                      .map((tier) => tier.toUpperCase())
                      .join(' / ')
                  : 'Disabled'
              }
            />
          </div>

          <div className="columns-2 gap-3 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6">
            {filtered.map((photo) => {
              const pick = picks[photo.id]
              const loved = pick?.bucket === 'love'
              const skipped = pick?.bucket === 'skip'
              const maybeMarked = pick?.bucket === 'maybe'
              const photoIndex = photoIndexById[photo.id] ?? 0
              const ratio = dimensions[photo.id] || 1.25
              const aspectRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1.25

              return (
                <div key={photo.id} className="mb-3 break-inside-avoid">
                  <div className="relative w-full" style={{ aspectRatio }}>
                    <button
                      onClick={() => navigate(`/photo/${Math.max(0, photoIndex)}`)}
                      className={[
                        'group absolute inset-0 block h-full w-full overflow-hidden rounded-lg bg-neutral-100 text-left shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-transform active:scale-[0.99]',
                        loved
                          ? 'ring-2 ring-[var(--accent-success)]'
                          : 'ring-1 ring-inset ring-black/[0.04]',
                        skipped && 'opacity-60',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-label={
                        reviewEnabled
                          ? `Open photo ${photo.id}, currently ${
                              pick?.bucket === 'love'
                                ? 'selected'
                                : pick?.bucket === 'maybe'
                                  ? 'undecided'
                                  : pick?.bucket === 'skip'
                                    ? 'rejected'
                                    : 'unrated'
                            }`
                          : `Open photo ${photo.id}`
                      }
                    >
                      <PhotoSurface
                        src={photo.thumbUrl}
                        onImageLoad={
                          photo.aspectW && photo.aspectH && photo.aspectW > 0 && photo.aspectH > 0
                            ? undefined
                            : ({ naturalWidth, naturalHeight }) =>
                                recordDimensions(photo.thumbUrl, naturalWidth, naturalHeight)
                        }
                        alt=""
                        rounded=""
                        className="h-full w-full transition-transform duration-500 group-hover:scale-[1.03]"
                        imgClassName="h-full w-full object-cover"
                        fit="cover"
                      />

                      <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/45 to-transparent opacity-80 transition-opacity group-hover:opacity-100" />
                      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/55 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

                      {photo.isRecommended && (
                        <div className="absolute left-2 top-2 flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
                          <Star size={9} fill="currentColor" strokeWidth={0} />
                          {reviewEnabled ? 'Pick' : 'Featured'}
                        </div>
                      )}
                      {loved && (
                        <div className="absolute right-2 top-2 grid h-[22px] w-[22px] place-items-center rounded-full bg-[var(--accent-success)] text-white shadow-md">
                          <Check size={14} strokeWidth={2.5} />
                        </div>
                      )}
                      {skipped && (
                        <>
                          <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px]" />
                          <div className="absolute left-1/2 top-1/2 z-10 grid h-9 w-9 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-black/80 text-white shadow-lg">
                            <X size={18} strokeWidth={2.4} />
                          </div>
                        </>
                      )}
                      {maybeMarked && (
                        <div className="absolute right-2 top-2 grid h-[22px] w-[22px] place-items-center rounded-full border border-[var(--border-strong)] bg-[var(--surface-quaternary)] text-[var(--text-primary)] shadow-md">
                          <span className="text-[10px] font-bold leading-none">M</span>
                        </div>
                      )}
                      <div className="absolute bottom-2 left-2 max-w-[calc(100%-4rem)] translate-y-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/90 opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100">
                        {photo.category || 'Gallery'}
                      </div>
                      <BrandingBadge size="sm" />
                    </button>
                    {policy && photo.downloadUrl && (
                      <div className="absolute bottom-2 right-2 z-20">
                        <PolicyDownloadMenu
                          policy={policy}
                          availability={
                            photo.downloadAvailability ??
                            (policy.policyVersion === 0 ? zipAvailability : {})
                          }
                          downloadUrl={photo.downloadUrl}
                          onDownload={(tier) => {
                            if (shareToken)
                              void trackShareEvent(shareToken, 'photo_downloaded', {
                                photoId: photo.id,
                                metadata: { source: 'gallery_photo', tier },
                              })
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {filtered.length === 0 && photos.length > 0 && (
            <div className="py-16 text-center text-sm text-[var(--text-tertiary)]">
              No photos match this filter yet.
            </div>
          )}
          {photos.length === 0 && (
            <div className="flex flex-col items-center gap-3 px-6 py-20 text-center text-[var(--text-tertiary)]">
              <ImageOff size={28} strokeWidth={1.6} aria-hidden />
              <div className="max-w-xs text-sm leading-snug">
                The photographer hasn't shared any photos here yet. Check back once you hear from
                them.
              </div>
            </div>
          )}
        </main>

        {/* Delivery sign-off bar · only on a picks-disabled (delivery) gallery
            with photos. Gives the couple a terminal action — Approve the set or
            ask for changes — that a delivery link otherwise lacks. */}
        {approveEnabled && photos.length > 0 && (
          <div
            data-testid="gallery-approve-bar"
            className="sticky bottom-0 z-30 border-t border-neutral-200 bg-[#fbfbfa]/95 px-5 py-3.5 backdrop-blur-xl sm:px-8 lg:px-10"
          >
            {approveError && (
              <div
                role="alert"
                className="mb-2 text-center text-[12px] text-[var(--accent-danger,#ef4444)]"
              >
                {approveError}
              </div>
            )}
            <div className="mx-auto flex max-w-3xl flex-col items-center gap-2.5 sm:flex-row sm:justify-between">
              <div className="text-[13px] text-neutral-600">
                Finished reviewing? Approve the gallery or request changes for{' '}
                {manifest.photographer.name}.
              </div>
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <button
                  type="button"
                  data-testid="gallery-request-changes"
                  onClick={() => void sendDecision('request_changes')}
                  disabled={approving !== null}
                  className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 text-[13px] font-semibold text-neutral-800 transition hover:border-neutral-400 disabled:opacity-50 sm:flex-none"
                >
                  {approving === 'request_changes' ? (
                    <Loader2 size={14} className="animate-spin" aria-hidden />
                  ) : (
                    <MessageSquare size={14} strokeWidth={2.2} aria-hidden />
                  )}
                  Request changes
                </button>
                <button
                  type="button"
                  data-testid="gallery-approve"
                  onClick={() => void sendDecision('approve')}
                  disabled={approving !== null}
                  className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full bg-[#111] px-5 text-[13px] font-semibold text-white transition hover:bg-black disabled:opacity-50 sm:flex-none"
                >
                  {approving === 'approve' ? (
                    <Loader2 size={14} className="animate-spin" aria-hidden />
                  ) : (
                    <ThumbsUp size={14} strokeWidth={2.2} aria-hidden />
                  )}
                  Approve gallery
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function HeroMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-black/20 px-4 py-3">
      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/55">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-white">{value}</div>
    </div>
  )
}

function GalleryEvidence({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 md:block">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
        {label}
      </div>
      <div className="mt-0 font-medium text-neutral-900 md:mt-1">{value}</div>
    </div>
  )
}
